import { themeTypes } from "@/src/constants/themeConstants";
import { themeIconMap } from "@/src/utils/themeIconMap";

const getEffectiveTheme = (state) => {
  return state.currentTheme === themeTypes.AUTO
    ? state.systemPreference
    : state.currentTheme;
};

const updateImageSrc = (element, effectiveTheme, getters) => {
  if (element.tagName === "IMG" && element.dataset.themeIcon) {
    try {
      const iconConfig = JSON.parse(element.dataset.themeIcon);
      element.src = getters.getIconForTheme(iconConfig, effectiveTheme);
    } catch (error) {
      console.error("Error updating theme image:", error);
    }
  }
};

const getStoredTheme = () => {
  try {
    const theme = localStorage.getItem("theme");
    const themeMap = {
      light: themeTypes.LIGHT,
      dark: themeTypes.DARK,
      auto: themeTypes.AUTO,
    };

    return themeMap[theme] || null;
  } catch (error) {
    return null;
  }
};

export default {
  namespaced: true,
  state: {
    currentTheme: themeTypes.LIGHT,
    systemPreference: null,
    availableThemes: [themeTypes.AUTO, themeTypes.LIGHT, themeTypes.DARK],
    themeMapper: themeIconMap,
  },
  mutations: {
    SET_THEME(state, theme) {
      if (!state.availableThemes.includes(theme)) {
        theme = themeTypes.LIGHT;
      }

      state.currentTheme = theme;

      document.documentElement.setAttribute(
        "data-theme",
        theme === themeTypes.AUTO
          ? state.systemPreference || themeTypes.LIGHT
          : theme,
      );
    },
    SET_SYSTEM_PREFERENCE(state, preference) {
      state.systemPreference = preference;

      if (state.currentTheme === themeTypes.AUTO) {
        document.documentElement.setAttribute("data-theme", preference);
      }
    },
  },
  actions: {
    setTheme({ commit, dispatch }, theme) {
      commit("SET_THEME", theme);
      dispatch("updateThemeImages");
    },
    initTheme({ commit, state, dispatch }, theme) {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");
      commit(
        "SET_SYSTEM_PREFERENCE",
        prefersDark.matches ? themeTypes.DARK : themeTypes.LIGHT,
      );

      prefersDark.addEventListener("change", (e) => {
        commit(
          "SET_SYSTEM_PREFERENCE",
          e.matches ? themeTypes.DARK : themeTypes.LIGHT,
        );
        if (state.currentTheme === themeTypes.AUTO) {
          dispatch("updateThemeImages");
        }
      });

      const storedTheme = getStoredTheme();
      let updatedTheme;

      if (storedTheme) {
        if (storedTheme === themeTypes.LIGHT) {
          localStorage.removeItem("theme");
        }
        updatedTheme = storedTheme;
      } else {
        updatedTheme = theme;
      }

      commit("SET_THEME", updatedTheme);
      dispatch("updateThemeImages");
    },
    updateThemeImages({ state, getters }) {
      const effectiveTheme = getEffectiveTheme(state);

      requestAnimationFrame(() => {
        document
          .querySelectorAll('[data-theme-aware="true"]')
          .forEach((element) =>
            updateImageSrc(element, effectiveTheme, getters),
          );
      });
    },
    registerThemeAwareImage({ state, getters }, element) {
      const effectiveTheme = getEffectiveTheme(state);
      updateImageSrc(element, effectiveTheme, getters);
    },
  },
  getters: {
    getCurrentTheme: (state) => state.currentTheme,
    getEffectiveTheme: (state) => {
      return state.currentTheme === themeTypes.AUTO
        ? state.systemPreference
        : state.currentTheme;
    },
    getIconForTheme:
      (state) =>
      (iconConfig, theme = null) => {
        const effectiveTheme =
          theme ||
          (state.currentTheme === themeTypes.AUTO
            ? state.systemPreference
            : state.currentTheme);

        if (effectiveTheme === themeTypes.LIGHT) {
          return iconConfig.lightIcon || iconConfig.darkIcon;
        } else {
          return iconConfig.darkIcon || iconConfig.lightIcon;
        }
      },
  },
};
